/**
 * Generate dynamic validation rules from form builder configuration
 * 
 * @param {Object} formBuilderConfig Form builder configuration object
 * @param {string} step Step to generate rules for (e.g., 'step_2' for billing)
 * @returns {Object} Validation descriptor
 */
export function generateFormBuilderRules(formBuilderConfig, step = 'step_2') {
	if (!formBuilderConfig || !formBuilderConfig.fields || !Array.isArray(formBuilderConfig.fields)) {
		return null;
	}

	// Filter fields for the specified step
	const stepFields = formBuilderConfig.fields.filter(field => {
		return field.step === step || (!field.step && step === 'step_2'); // Default to step_2 if no step specified
	});

	if (stepFields.length === 0) {
		return null;
	}

	// Build dynamic billing_fields validation
	const billingFieldsRules = {};
	
	stepFields.forEach(field => {
		if (!field.name) return;
		
		const fieldRule = {
			type: getFieldValidationType(field.type),
		};

		// Only add required validation if field is marked as required
		if (field.required === true) {
			fieldRule.required = true;
			fieldRule.message = field.label 
				? `${field.label} is required` 
				: `${field.name} is required`;
		}

		// Add email validation for email fields
		if (field.type === 'email') {
			fieldRule.type = 'email';
			if (!fieldRule.message) {
				fieldRule.message = field.label 
					? `${field.label} is required and must be a valid email` 
					: 'Email is required and must be a valid email';
			}
		}

		billingFieldsRules[field.name] = fieldRule;
	});

	// Return validation descriptor compatible with async-validator
	return {
		amount: {
			type: "number",
			required: true,
			message: "Amount is required and must be a valid number",
			validator: (rule, value) => value > 0,
			transform(value) {
				return parseFloat(value);
			},
		},
		payment_method: {
			type: "string",
			required: true,
			message: "Please select the payment method",
		},
		reucrring: { 
			type: 'enum', 
			enum: [true, false] 
		},
		billing_fields: {
			type: 'object',
			required: true,
			fields: billingFieldsRules
		},
	};
}

/**
 * Get validation type based on field type
 * 
 * @param {string} fieldType Field type from form builder
 * @returns {string} Validation type for async-validator
 */
function getFieldValidationType(fieldType) {
	const typeMap = {
		'text': 'string',
		'email': 'email',
		'tel': 'string',
		'phone': 'string',
		'textarea': 'string',
		'select': 'string',
		'checkbox': 'boolean',
		'anonymous_donation': 'boolean',
	};

	return typeMap[fieldType] || 'string';
}

/**
 * Get default validation descriptor (for legacy forms)
 * 
 * @returns {Object} Default validation descriptor
 */
export function getDefaultRules() {
	return {
		amount: {
			type: "number",
			required: true,
			message: "Amount is required and must be a valid number",
			validator: (rule, value) => value > 0,
			transform(value) {
				return parseFloat(value);
			},
		},
		payment_method: {
			type: "string",
			required: true,
			message: "Please select the payment method",
		},
		reucrring: { type: 'enum', enum: [true, false] },
		billing_fields: {
			type: 'object',
			required: true,
			fields: {
				first_name: { type: "string", required: true, message: 'First name is required' },
				last_name: { type: "string", required: true, message: 'Last name is required' },
				address: { type: "string", required: true, message: 'Address is required' },
				email: { type: "email", required: true, message: 'Email is required and must be a valid email' },
			}
		},
	};
}
